import React, { useState, useEffect } from 'react';
import {
  VerificationContainer,
  VerificationTitle,
  VerificationDescription,
  ChangeNumberLink,
  CodeInputContainer,
  CodeInput,
  ResendContainer,
  ResendText,
  ResendButton,
  ErrorMessage,
  SuccessMessage,
  VerificationButton,
  Label,
  DeliveryOptions,
  DeliveryOption,
} from './styles/contact-form-styles';
import WhatsAppIcon from '../../images/icons/whatsapp.svg';
import PhoneCallIcon from '../../images/icons/phone-call.svg';

const PhoneVerificationStep = ({
  phoneNumber,
  channel = 'whatsapp',
  onCodeSubmit,
  onResendCode,
  onChangeNumber,
  isLoading = false,
  error = null,
  success = false,
}) => {
  const [code, setCode] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(channel || 'whatsapp');

  // Countdown para reenvio de código
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleCodeChange = e => {
    const rawValue = e.target.value || ''; // Garantir que não seja undefined
    const value = rawValue?.replace(/[^A-Za-z0-9]/g, '')?.toUpperCase(); // Apenas letras e números
    if (value?.length <= 6) {
      setCode(value);

      // ✅ Auto-submit quando código tiver 6 caracteres
      if (value.length === 6) {
        onCodeSubmit(value);
      }
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    // ✅ Removido - agora é automático quando código tem 6 caracteres
  };

  const handleResendCode = async () => {
    if (resendCountdown === 0 && !isResending) {
      setIsResending(true);
      try {
        await onResendCode(selectedChannel);
      } catch (error) {
        console.error('Erro ao reenviar código:', error);
      } finally {
        setIsResending(false);
      }
    }
  };

  const handleChannelChange = async (newChannel) => {
    setSelectedChannel(newChannel);
    
    if (onResendCode) {
      setIsResending(true);
      try {
        await onResendCode(newChannel);
        setResendCountdown(60); // Inicia countdown após envio
      } catch (error) {
        console.error('Erro ao enviar código:', error);
      } finally {
        setIsResending(false);
      }
    }
  };

  const formatPhoneNumber = phone => {
    if (!phone) return '';
    // Formatar número para exibição: +5511 00000 0000
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length >= 11) {
      return `+55${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)} ${cleaned.slice(7, 11)}`;
    } else if (cleaned.length >= 10) {
      return `+55${cleaned.slice(0, 2)} ${cleaned.slice(2, 6)} ${cleaned.slice(6, 10)}`;
    }
    return phone;
  };

  return (
    <VerificationContainer>
      <VerificationTitle>
        Antes de continuar, precisamos ter certeza de <br />
        que o seu número de celular está correto.
      </VerificationTitle>

      <VerificationDescription>
        <div style={{ fontWeight: 700, marginTop: 6 }}>Vamos enviar um código de segurança para confirmação.</div>
      </VerificationDescription>

  <div style={{ margin: '12px 0 6px 0', color: '#333', fontWeight: 600 }}>Escolha como deseja receber o código:</div>
  <DeliveryOptions>
        <DeliveryOption
          className={selectedChannel === 'whatsapp' ? 'selected' : ''}
          onClick={() => handleChannelChange('whatsapp')}
        >
          <input type="radio" name="deliveryMethod" value="whatsapp" checked={selectedChannel === 'whatsapp'} readOnly />
          <span className="iconBadge whatsapp">
            <img src={WhatsAppIcon} alt="WhatsApp" />
          </span>
          <span>WhatsApp</span>
        </DeliveryOption>

        <DeliveryOption
          className={selectedChannel === 'call' ? 'selected' : ''}
          onClick={() => handleChannelChange('call')}
        >
          <input type="radio" name="deliveryMethod" value="call" checked={selectedChannel === 'call'} readOnly />
          <span className="iconBadge call">
            <img src={PhoneCallIcon} alt="Ligação Telefônica" />
          </span>
          <span>{'Ligação\u00A0Telefônica'}</span>
        </DeliveryOption>
      </DeliveryOptions>

      <div style={{ marginTop: 18, display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <div style={{ color: '#333', fontSize: '14px', lineHeight: 1.2 }}>
          {selectedChannel === 'call' ? (
            <>Você receberá uma ligação no número {formatPhoneNumber(phoneNumber)} com o código de verificação.</>
          ) : (
            <>Insira abaixo o código informado por WhatsApp para {formatPhoneNumber(phoneNumber)}.</>
          )}
        </div>
        <ChangeNumberLink onClick={onChangeNumber} style={{ marginLeft: 8, marginTop: 4 }}>Alterar número</ChangeNumberLink>
      </div>

      <form onSubmit={handleSubmit}>
        <CodeInputContainer>
          <Label>Código</Label>
          <CodeInput
            type="text"
            value={code}
            onChange={handleCodeChange}
            placeholder="Ex: A1B2C3"
            maxLength={6}
            disabled={isLoading}
          />
        </CodeInputContainer>

        <hr style={{ marginTop: 10, border: 'none', borderTop: '1px solid #e6e6e6', margin: '20px 0' }} />
        <ResendContainer>
          <div>
            <strong>Não recebeu a mensagem com o código?</strong>
          </div>
          <div style={{ marginTop: 8 }}>
            <ResendButton type="button" onClick={handleResendCode} disabled={isResending || resendCountdown > 0}>
              {isResending ? 'Reenviando...' : 'Solicitar nova mensagem'}
            </ResendButton>
          </div>
          {resendCountdown > 0 && <ResendText style={{ marginTop: 8 }}>Você poderá reenviar o código novamente em {resendCountdown}s</ResendText>}
        </ResendContainer>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>Código verificado com sucesso!</SuccessMessage>}
      </form>
    </VerificationContainer>
  );
};

export default PhoneVerificationStep;
